// =============================================================
// Notification center (Phase 9.3 global chrome) — topbar bell + unread badge +
// dropdown feed. Reads the DERIVED feed from GET /notifications (overdue WOs,
// low stock, open faults, parked sync items) and marks items read via
// POST /notifications/read (per-principal). Clicking an item navigates to it.
// =============================================================

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AppNotification } from "@perpetuum/core";
import { useMarkNotificationsRead, useNotifications } from "../lib/queries.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Icon, ICONS } from "./primitives.js";
import { EmptyState } from "./EmptyState.js";
import { Skeleton } from "./Skeleton.js";

type Translate = (key: string, vars?: Record<string, string | number>) => string;

const KIND_ICON: Record<AppNotification["kind"], string> = {
  overdue_wo: ICONS.clock,
  low_stock: ICONS.box,
  fault: ICONS.alert,
  sync_review: ICONS.inbox,
};

function timeAgo(iso: string, t: Translate): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const sec = Math.round((Date.now() - then) / 1000);
  if (sec < 60) return t("notif.ago.justNow");
  const min = Math.round(sec / 60);
  if (min < 60) return t("notif.ago.min", { n: min });
  const hr = Math.round(min / 60);
  if (hr < 24) return t("notif.ago.hour", { n: hr });
  return t("notif.ago.day", { n: Math.round(hr / 24) });
}

export function NotificationCenter() {
  const t = useScopedT();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useNotifications();
  const markRead = useMarkNotificationsRead();

  const unread = data?.unread ?? 0;
  const items = data?.data ?? [];

  // Poll on open: re-fetch the feed the moment the panel is opened so the list
  // is fresh (the hook also light-polls on a 30s interval while mounted).
  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);

  // Close on outside click + Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onItemClick = (n: AppNotification) => {
    if (!n.read) markRead.mutate({ key: n.key });
    if (n.path) {
      navigate(n.path);
      setOpen(false);
    }
  };

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        className="notif-bell"
        aria-label={unread ? t("notif.bellUnread", { n: unread }) : t("notif.bell")}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid="notif-bell"
      >
        <Icon d={ICONS.bell} size={18} />
        {unread > 0 && (
          <span className="notif-badge" data-testid="notif-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label={t("notif.label")} data-testid="notif-panel">
          <div className="notif-head">
            <h3>{t("notif.label")}</h3>
            <button
              className="notif-markall"
              disabled={unread === 0 || markRead.isPending}
              onClick={() => markRead.mutate({ all: true })}
              data-testid="notif-markall"
            >
              {t("notif.markAll")}
            </button>
          </div>
          <div className="notif-list">
            {isLoading && (
              <div style={{ padding: "10px 14px" }} data-testid="notif-loading">
                <Skeleton variant="text" count={3} />
              </div>
            )}
            {!isLoading && items.length === 0 && (
              <div data-testid="notif-empty">
                <EmptyState compact icon={ICONS.bell} title={t("notif.empty")} />
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.key}
                className={`notif-item ${n.read ? "read" : "unread"} sev-${n.severity}`}
                onClick={() => onItemClick(n)}
                data-testid={`notif-item-${n.key}`}
              >
                <span className="notif-ic">
                  <Icon d={KIND_ICON[n.kind] ?? ICONS.bell} size={16} />
                </span>
                <span className="notif-body">
                  <span className="notif-title">{n.title}</span>
                  {n.detail && <span className="notif-detail">{n.detail}</span>}
                </span>
                <span className="notif-meta">
                  {!n.read && <span className="notif-dot" aria-label={t("notif.unread")} />}
                  <span className="notif-time">{timeAgo(n.at, t)}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
